import { useState } from 'react';

import { Button } from '@/components/common/Button';
import type { CreateShareOptions, ShareExpiration } from '@/types/share';

interface CreateShareFormProps {
  onSubmit: (options: CreateShareOptions) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const EXPIRATION_OPTIONS: { value: ShareExpiration; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'never', label: 'Never' },
];

export function CreateShareForm({ onSubmit, onCancel, isSubmitting }: CreateShareFormProps) {
  const [label, setLabel] = useState('');
  const [includeScouting, setIncludeScouting] = useState(true);
  const [includePhotos, setIncludePhotos] = useState(false);
  const [expiration, setExpiration] = useState<ShareExpiration>('30d');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSubmit({
      label: label.trim() || undefined,
      includeScouting,
      includePhotos,
      expiration,
      password: password.trim() || undefined,
    });
  }

  const labelClass =
    'field-label';
  const inputClass =
    'w-full px-3 py-2 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent';

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-5" data-testid="create-share-form">
      {/* Label */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="share-label" className={labelClass}>
          Label <span className="font-normal normal-case text-[var(--color-text-muted)]">(optional)</span>
        </label>
        <input
          id="share-label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder='e.g. "For Dave" or "Hunting group"'
          maxLength={80}
          className={inputClass}
          data-testid="label-input"
        />
      </div>

      {/* Toggles */}
      <div className="flex flex-col gap-3">
        <p className={labelClass}>Include in shared view</p>

        {/* Scouting log toggle */}
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <span className="text-body-sm text-[var(--color-text-primary)]">Scouting log</span>
          <button
            type="button"
            role="switch"
            aria-checked={includeScouting}
            onClick={() => setIncludeScouting((v) => !v)}
            data-testid="include-scouting-toggle"
            // M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
            className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px] ${
              includeScouting
                ? 'bg-[var(--color-accent)]'
                : 'bg-[var(--color-border)]'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 self-center w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                includeScouting ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </label>

        {/* Photos toggle */}
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div className="flex flex-col gap-0.5">
            <span className="text-body-sm text-[var(--color-text-primary)]">Photos</span>
            <span className="text-body-xs text-[var(--color-text-muted)]">Off by default — trail cam photos reveal property details</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={includePhotos}
            onClick={() => setIncludePhotos((v) => !v)}
            data-testid="include-photos-toggle"
            // M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
            className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px] shrink-0 ${
              includePhotos
                ? 'bg-[var(--color-accent)]'
                : 'bg-[var(--color-border)]'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 self-center w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                includePhotos ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </label>
      </div>

      {/* Expiration */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="share-expiration" className={labelClass}>
          Link expires after
        </label>
        <select
          id="share-expiration"
          value={expiration}
          onChange={(e) => setExpiration(e.target.value as ShareExpiration)}
          className={inputClass}
          data-testid="expiration-select"
        >
          {EXPIRATION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Password */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="share-password" className={labelClass}>
          Password <span className="font-normal normal-case text-[var(--color-text-muted)]">(optional)</span>
        </label>
        <div className="relative">
          <input
            id="share-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Leave blank for no password"
            className={`${inputClass} pr-16`}
            data-testid="password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-body-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 justify-end pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={isSubmitting} type="button">
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating…' : 'Create link'}
        </Button>
      </div>
    </form>
  );
}
